import type { SessionView } from '~/helpers/learner/session-view'
import { CheckCircle2, Clock, PlayCircle, Target } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '~/components/ui/button'
import { Progress } from '~/components/ui/progress'
import { useBranding } from '~/helpers/branding/branding-context'
import { DATE_WITH_YEAR, formatSessionDate } from '~/helpers/dates/session-dates'
import { canDownloadCertificate } from '~/helpers/learner/certificate-notice'
import { formatMinutes, formatSeconds, remainingMinutes } from '~/helpers/learner/duration'
import { completionStatus, courseLaunchHref, distinctSubtitle, nextStep, progressPct, sessionHref, STEP_ACTION_KEYS, stepAction, steps, stepScore, stepThreshold, timeSpentSeconds } from '~/helpers/learner/session-view'
import CertificateButton from '~/pages/scorm/components/CertificateButton'
import CertificateLockNotice from '~/pages/scorm/components/CertificateLockNotice'
import LockNotice from '~/pages/scorm/components/LockNotice'
import MetaLine from '~/pages/scorm/components/MetaLine'
import ScoreValue from '~/pages/scorm/components/ScoreValue'
import SessionCardFrame from '~/pages/scorm/components/SessionCardFrame'

type IProps = {
  session: SessionView & {
    progressionMode?: string | null
  }
}

/**
 * Carte d'un PARCOURS : une vitrine, pas la liste complète des étapes.
 *
 * Elle dit où l'on en est et par quoi reprendre, puis renvoie à l'écran du
 * parcours. La liste entière vivait ici, écrasée dans une colonne de grille :
 * elle n'avait tout simplement pas la place d'être lue.
 */
export function PathCard({ session }: IProps) {
  const { t, i18n } = useTranslation()

  const all = steps(session)
  const done = session.progress?.completedModules ?? 0
  const total = session.progress?.totalModules ?? all.length
  const next = nextStep(session)
  const completed = completionStatus(session) === 'COMPLETED'
  // Première étape fermée : c'est elle qui explique pourquoi on ne peut pas
  // continuer aujourd'hui, quand `nextStep` ne rend rien.
  const blocked = all.find(s => s.unlocked === false)

  // Ce qu'il reste à faire, pas la durée totale du parcours : l'apprenant qui
  // revient sait ce qu'il a déjà donné, il veut savoir ce qui l'attend encore.
  const remaining = formatMinutes(remainingMinutes(all), t)
  const spent = formatSeconds(timeSpentSeconds(session), t)
  const href = sessionHref(session)!
  const pct = progressPct(session)
  // Terminé avec son attestation : la télécharger devient l'action principale (#307).
  const certificateFirst = completed && canDownloadCertificate(session)

  return (
    <SessionCardFrame
      visualId={session.id}
      kindLabel={t('home.type.path', { count: total })}
      session={session}
      href={href}
      actions={(
        <>
          {certificateFirst && <CertificateButton sessionId={session.id} primary />}
          <Button asChild variant={certificateFirst ? 'outline' : 'default'} className="flex-1 basis-40">
            <Link to={href}>
              <PlayCircle className="mr-2 size-4" />
              {completed ? t('home.review') : t('home.continue')}
            </Link>
          </Button>
          {!certificateFirst && canDownloadCertificate(session) && <CertificateButton sessionId={session.id} />}
        </>
      )}
    >
      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-2 text-sm">
          <span className="text-muted-foreground">{t('home.stepsDone', { done, total })}</span>
          <span className="font-semibold tabular-nums">
            {new Intl.NumberFormat(i18n.language, { style: 'percent' }).format(pct / 100)}
          </span>
        </div>
        <Progress value={pct} className="h-1.5" aria-label={t('path.progress.label')} />
      </div>
      <CertificateLockNotice session={session} />

      <MetaLine
        items={[
          remaining ? { icon: Clock, label: t('home.meta.remaining', { duration: remaining }) } : null,
          spent ? { icon: Clock, label: t('home.meta.spent', { duration: spent }) } : null,
        ]}
      />

      {next
        ? (
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <PlayCircle className="mt-0.5 size-4 shrink-0" />
              <span className="min-w-0">
                {t('home.nextStep')}
                {' '}
                <span className="text-foreground">{next.module?.title}</span>
              </span>
            </p>
          )
        : completed
          ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="size-4 shrink-0 text-success-text" />
                {t('home.allStepsDone')}
              </p>
            )
          : blocked
            ? (
                <LockNotice step={blocked} steps={all} />
              )
            : null}
    </SessionCardFrame>
  )
}

/**
 * Carte d'un COURS : un seul module, donc la carte EST l'objet.
 *
 * Un clic lance. Lui donner un écran de détail serait une cérémonie pour un
 * module unique — c'est ce qui distingue cette carte de celle d'un parcours,
 * bien plus que sa décoration.
 */
export function CourseCard({ session }: IProps) {
  const { t, i18n } = useTranslation()
  const { timezone } = useBranding()
  const navigate = useNavigate()

  const single = steps(session)[0]
  const status = completionStatus(session)
  const completed = status === 'COMPLETED'
  const tracking = single?.myTracking
  const launchHref = courseLaunchHref(session)
  const action = single ? stepAction(single) : null

  const estimated = formatMinutes(single?.module?.durationMinutes, t)
  const spent = formatSeconds(single?.myTracking?.totalTimeSeconds, t)
  const threshold = single ? stepThreshold(single) : null
  const score = single ? stepScore(single) : null
  const certificateFirst = completed && canDownloadCertificate(session)

  return (
    <SessionCardFrame
      visualId={single?.module?.id ?? session.id}
      kindLabel={t('home.type.course')}
      session={session}
      subtitle={distinctSubtitle(session.name, single?.module?.title)}
      // Terminée sans pouvoir être refaite : la carte n'est plus un lien (#84).
      href={action ? launchHref : undefined}
      actions={(
        <>
          {/* Terminée et que le parcours interdit de refaire : plus de bouton de
              lancement, l'attestation reste (#84). */}
          {certificateFirst && <CertificateButton sessionId={session.id} primary />}
          {action && (
            <Button
              variant={certificateFirst ? 'outline' : 'default'}
              className="flex-1 basis-40"
              disabled={!launchHref}
              onClick={() => launchHref && navigate(launchHref)}
            >
              <PlayCircle className="mr-2 size-4" />
              {t(STEP_ACTION_KEYS[action])}
            </Button>
          )}
          {!certificateFirst && canDownloadCertificate(session) && <CertificateButton sessionId={session.id} />}
        </>
      )}
    >
      <CertificateLockNotice session={session} />
      <MetaLine
        items={[
          estimated ? { icon: Clock, label: t('home.meta.estimated', { duration: estimated }) } : null,
          spent ? { icon: Clock, label: t('home.meta.spent', { duration: spent }) } : null,
          threshold != null ? { icon: Target, label: t('home.meta.threshold', { threshold }) } : null,
        ]}
      />

      {(score || (completed && tracking?.completedAt != null)) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {score && (
            <span>
              {t('common.score')}
              {' : '}
              <ScoreValue score={score} />
            </span>
          )}
          {completed && tracking?.completedAt != null && (
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 className="size-3.5 text-success-text" />
              {t('home.completedOn')}
              {' '}
              {formatSessionDate(tracking.completedAt, i18n.language, timezone, DATE_WITH_YEAR)}
            </span>
          )}
        </div>
      )}

      {single?.unlocked === false && (
        <LockNotice step={single} steps={steps(session)} />
      )}
    </SessionCardFrame>
  )
}
